<template>
    <div>
        <vue-multiselect
            ref="multiselect"
            :loading="loadingOptions"
            v-bind="mergedProps"
            v-on="commonEvents"
            @open="handleOpen">
            <template v-slot:noOptions>
                Keine Einträge vorhanden.
            </template>
            <template v-slot:noResult>
                Keine Übereinstimmungen gefunden.
            </template>

            <template v-slot:singleLabel="props">
                <span v-if="props.option.icon"><i :class="props.option.icon"/></span>
                <span v-if="props.option.$isLabel">
                    <span><b>{{ toUpperCase(props.option.$groupLabel) }}</b></span>
                </span>
                <span v-else>
                    <span :class="transformClasses(props.option.cssClasses)">{{ props.option.text }}</span>
                </span>
            </template>

            <template v-slot:option="props">
                <span v-if="props.option.icon"><i :class="props.option.icon"/></span>
                <span v-if="props.option.$isLabel">
                    <span><b>{{ toUpperCase(props.option.$groupLabel) }}</b></span>
                </span>
                <span v-else>
                    <span :class="transformClasses(props.option.cssClasses)">{{ props.option.text }}</span>
                </span>
            </template>
        </vue-multiselect>
    </div>
</template>

<script>
    import {idMixin} from 'bootstrap-vue/src/mixins/id';
    import VueMultiselect from 'vue-multiselect';
    import '@scss/components/select-searchable.scss';
    import {selectDeselectAllOptionValue} from '@components/form/selectSearchableConstants';

    export default {
        components: {
            'vue-multiselect': VueMultiselect,
        },
        mixins: [idMixin],
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            openDirection: {
                type: String,
                default: '',
            },
            options: {
                type: Array,
                default: () => [],
            },
            placeholder: {
                type: String,
                required: false,
                default: '',
            },
            resetAfter: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                searchTerm: '',
            };
        },
        computed: {
            baseOptions() {
                return this.internalOptions !== undefined ? this.internalOptions : this.options;
            },
            mergedProps() {
                return this.mergeProps(this.commonProps, this.customProps);
            },
            commonProps() {
                const props = {
                    id: this.safeId(),
                    deselectLabel: this.deselectLabel,
                    disabled: this.disabled,
                    internalSearch: false,
                    label: 'text',
                    name: this.safeId(),
                    openDirection: this.openDirection,
                    options: this.filteredOptions,
                    optionHeight: this.$root.multiselectOptionHeight,
                    placeholder: this.placeholderOrDefault,
                    resetAfter: this.resetAfter,
                    selectLabel: this.$t('form.multiselect.select'),
                    selectedLabel: 'Aktiv',
                    trackBy: 'value',
                };

                if (this.isUsingOptionGroups) {
                    props.selectGroupLabel = 'Gruppe auswählen';
                    props.deselectGroupLabel = 'Gruppe abwählen';
                    props.groupValues = 'options';
                    props.groupLabel = 'optionGroup';
                    props.groupSelect = false;
                }

                return props;
            },
            commonEvents() {
                return {
                    input: this.handleInput,
                    'search-change': this.handleSearchChange,
                };
            },
            customProps() {
                return {};
            },
            deselectLabel() {
                return this.$t('form.multiselect.deselect');
            },
            filteredOptions() {
                if (this.searchTerm === '') {
                    return this.isUsingOptionGroups
                        ? this.reorderOptionGroups(this.baseOptions)
                        : this.reorderOptions(this.baseOptions);
                }

                const searchTerms = (this.searchTerm || '').toLocaleLowerCase().split(' ');

                if (!this.isUsingOptionGroups) {
                    return this.getFilteredOptions(this.baseOptions, searchTerms);
                }

                return this.getFilteredOptionGroups(this.baseOptions, searchTerms);
            },
            isUsingOptionGroups() {
                if (!this.baseOptions.length) {
                    return false;
                }
                return !!this.baseOptions[0].options;
            },
            placeholderOrDefault() {
                if (this.placeholder) {
                    return this.placeholder;
                }
                return this.$t('form.multiselect.placeholder');
            },
        },
        mounted() {
            this.initializeSelected();

            // vue-multiselect does not support a different placeholder for the search input.
            // TODO: create pull request for Vue Multiselect
            this.$nextTick(() => {
                const searchInput = this.$refs?.multiselect?.$refs?.search;

                if (searchInput instanceof HTMLElement) {
                    searchInput.setAttribute('placeholder', 'in Auswahl suchen...');
                }
            });
        },
        methods: {
            reorderOptions(options) {
                if (!Array.isArray(options)) {
                    return [];
                }

                const pinnedItem = this.getPinnedFirstOption(options);
                const sortableItems = pinnedItem ? options.slice(1) : options.slice();

                const selectedItems = sortableItems.filter((option) => this.isOptionSelected(option));
                const unselectedItems = sortableItems.filter((option) => !this.isOptionSelected(option));

                return pinnedItem
                    ? [pinnedItem, ...selectedItems, ...unselectedItems]
                    : [...selectedItems, ...unselectedItems];
            },
            reorderOptionGroups(groups) {
                if (!Array.isArray(groups)) {
                    return [];
                }

                return groups.map((group) => {
                    const clonedGroup = {...group};
                    const groupOptions = Array.isArray(group.options) ? group.options : [];

                    const pinnedItem = this.getPinnedFirstOption(groupOptions);
                    const sortableItems = pinnedItem ? groupOptions.slice(1) : groupOptions.slice();

                    const selectedItems = sortableItems.filter((option) => this.isOptionSelected(option));
                    const unselectedItems = sortableItems.filter((option) => !this.isOptionSelected(option));

                    clonedGroup.options = pinnedItem
                        ? [pinnedItem, ...selectedItems, ...unselectedItems]
                        : [...selectedItems, ...unselectedItems];

                    return clonedGroup;
                });
            },
            getPinnedFirstOption(options) {
                if (!Array.isArray(options) || !options.length) {
                    return null;
                }

                const firstOption = options[0];
                if (!firstOption || typeof firstOption !== 'object') {
                    return null;
                }

                return firstOption.value === selectDeselectAllOptionValue ? firstOption : null;
            },
            getFilteredOptions(options, searchTerms) {
                searchTerms.forEach((term) => {
                    options = options.filter((option) => option.text.toLowerCase().includes(term));
                });
                return options;
            },
            getFilteredOptionGroups(optionGroups, searchTerms) {
                // Clone option groups so filtering does not mutate the original array.
                let clonedOptionGroups = [];
                optionGroups.forEach((optionGroup) => {
                    clonedOptionGroups.push({...optionGroup});
                });

                searchTerms.forEach((term) => {
                    clonedOptionGroups = clonedOptionGroups.filter((optionGroup) => {
                        if (!optionGroup.options?.length) {
                            return false;
                        }

                        optionGroup.options = optionGroup.options.filter((option) => {
                            return option.text.toLowerCase().includes(term);
                        });

                        return optionGroup.options.length > 0;
                    });
                });

                return clonedOptionGroups;
            },
            getFlatOptions(options) {
                if (!Array.isArray(options)) {
                    return [];
                }

                if (!this.isUsingOptionGroups) {
                    return options;
                }

                return options.reduce((result, group) => {
                    if (Array.isArray(group?.options)) {
                        result.push(...group.options);
                    }
                    return result;
                }, []);
            },
            handleOpen() {
                // Lazy-load the full list on first open (mixin present in the wrapper).
                if (typeof this.ensureOptionsLoaded === 'function') {
                    this.ensureOptionsLoaded();
                }

                // Workaround to scroll to the first selected option:
                // https://github.com/shentao/vue-multiselect/issues/1021#issuecomment-632097606
                // TODO: remove when fixed in upstream.
                const multiselect = this.$refs.multiselect;
                const firstSelectedIndex = multiselect.filteredOptions.findIndex((option) => {
                    if (!option || option.$isLabel) {
                        return false;
                    }
                    return this.isOptionSelected(option);
                });

                if (firstSelectedIndex < 1) {
                    return;
                }

                multiselect.pointerSet(firstSelectedIndex);
                this.$nextTick(() => {
                    multiselect.$refs.list.scrollTop = multiselect.pointerPosition;
                });
            },
            handleSearchChange(query) {
                this.searchTerm = query;
            },
            handleInput() {
                // Override in concrete component.
            },
            initializeSelected() {
                // Override in concrete component.
            },
            isOptionSelected() {
                // Override in concrete component.
            },
            mergeProps(...sources) {
                // Merge objects, preserving reactivity.
                const result = {};
                sources.forEach((source) => {
                    Object.entries(source || {}).forEach(([key, value]) => {
                        if (value !== undefined) {
                            result[key] = value;
                        }
                    });
                });
                return result;
            },
            toUpperCase(value) {
                if (!value) {
                    return '';
                }
                value = value.toString();
                return value.toUpperCase();
            },
            transformClasses(classes) {
                if (!classes) {
                    return;
                }

                classes = classes.split(' ');
                let transformedClasses = {};
                classes.forEach((element) => {
                    transformedClasses[element] = true;
                });

                return transformedClasses;
            },
        },
    };
</script>
